<script setup lang='ts'>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { SyncOutlined } from '@ant-design/icons-vue'
import { onWindowFocus } from '@utopia-utils/core'
import { ScrollContainer } from '../ScrollContainer'
import { Download } from '@element-plus/icons-vue'

import { apiTaskQuery } from '@mp/api/downloadCenter/TaskController.ts'

import { downloadCenterEvent } from './event'
import FileItem from './FileItem.vue'
import { parabolicBall } from '@mp/utils'

const defaultSize = 15
const totalCount = ref(0)

const visible = ref(false)
const loading = ref(false)
const isError = ref(false)
const isShowAll = ref(false)

const taskListSource = ref<APIDownloadCenter.TbDownloadQueryOwnerModel[]>([])

const taskList = computed(() => {
  if (isShowAll.value)
    return taskListSource.value
  return taskListSource.value.slice(0, defaultSize)
})

async function fetchTaskList() {
  try {
    loading.value = true
    const res = await apiTaskQuery()
    if (res.resultCode === 'FAIL')
      isError.value = true
    else
      isError.value = false

    taskListSource.value = res.data || []
    totalCount.value = res.data?.length || 0
  }
  catch (err) {
    console.error(err)
    isError.value = true
  }
  finally {
    loading.value = false
  }
}
fetchTaskList()
// 重新获取焦点时刷新
onWindowFocus(fetchTaskList)

watch(visible, (val) => { // 打开时，立刻刷新一次列表
  if (val)
    fetchTaskList()
})

watch(visible, (val) => {
  document.body.style.overflow = val ? 'hidden' : ''
  if (!val)
    isShowAll.value = false
})

onBeforeUnmount(() => {
  document.body.style.overflow = ''
})

// 定时刷新
const timer = setInterval(fetchTaskList, 1000 * 60 * 5)
onBeforeUnmount(() => timer && clearInterval(timer))

async function onAddTaskSuccess(el?: HTMLElement | void) {
  fetchTaskList()

  if (!el)
    return
  parabolicBall(el, document.getElementById('downloadCount') as HTMLElement)
}

onMounted(() => {
  downloadCenterEvent.on('addTaskSuccess', onAddTaskSuccess)
  onBeforeUnmount(() => {
    downloadCenterEvent.off('addTaskSuccess', onAddTaskSuccess)
  })
})
</script>

<template>
  <Teleport to="body">
    <el-popover id="messageCenterPopover" v-model:visible="visible" popper-class="download-center-popover" style="--el-popper-border-radius: 10px;" :tabindex="999" teleported trigger="click" :width="500" placement="top">
      <div class="download-center-panel" @wheel.stop>
        <div class="flex items-center justify-between px-2 py-2">
          <div class="flex items-center gap-2">
            <el-icon color="var(--el-color-primary)" :size="18">
              <Download />
            </el-icon>
            <div class="text-base text-n-800">
              下载中心
              <span class="text-#ccc text-xs font-normal">（近 7 天）</span>
            </div>
          </div>
          <el-button link size="large" @click="fetchTaskList">
            <SyncOutlined :spin="loading" />
          </el-button>
        </div>

        <ScrollContainer :style="{ height: '450px' }">
          <el-result v-if="isError" icon="info" title="数据加载错误" />
          <el-empty v-else-if="!taskListSource.length" class="mt-10" description="暂无下载内容" />

          <template v-else>
            <div v-for="item in taskList" :key="item.downloadId">
              <FileItem :item="item" />
            </div>
            <template v-if="taskListSource?.length > defaultSize && !isShowAll">
              <div class="text-center mt-2">
                <el-button link type="primary" @click="isShowAll = true">
                  加载所有
                </el-button>
              </div>
            </template>
          </template>
        </ScrollContainer>
      </div>

      <template #reference>
        <div class="bottom-16 right-6 fixed z-10 text-center">
          <el-button :icon="Download" circle size="large" />
          <el-tag v-if="totalCount" id="downloadCount" size="small" round type="danger" effect="dark" class="absolute -right-1 -top-2 px-[6px]">
            {{ totalCount }}
          </el-tag>
          <p class="text-xs text-n-400 mt-1">
            下载中心
          </p>
        </div>
      </template>
    </el-popover>
  </Teleport>
</template>

<style>
.download-center-popover {
  overscroll-behavior: contain;
  transform: translateX(-5px);
}
.download-center-popover .scrollbar__wrap {
  overscroll-behavior: contain;
  padding-left: 10px;
  padding-right: 10px;
}
.download-center-animation-point-wrap {
  display: inline-block;
  position: absolute;
  z-index: 2000;
  transition: transform .6s;
  transition-timing-function: linear;
}
.download-center-animation-point {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #ff4d4f;
  transition: transform .6s;
  transition-timing-function: cubic-bezier(.55,0,.85,.36);
}
</style>
